import axiosApi from '@mint-lib/api';
import { formCustomRTKQueryError } from '@mint-lib/rtk-query-client';

import { PREDICTIVE_PLANNING_CREATE_AND_UPDATE } from '../constants/EndpointConstants.js';
import { QueryKey } from '../constants/queryKeys.js';
import {
  MediaPlanRowBudget,
  UpdateRowLockedPayload,
  UpdateRowPayload,
} from '../redux/reducer/campaigns/mediaPlans/types.js';
import {
  UpdateRowRequest,
  UpdateRowResponse,
} from '../types/_campaigns/campaignBuilder.js';
import { BlobResponse } from '../types/co2notifications.js';
import type { BaseQueryError } from '../types/shared.js';
import { transformValidationErrorResponse } from '../utils/helpers.js';
import { apiSlice, BASE_URL } from './apiSlice.js';
import { queryClient } from './queries/client.js';

// These are workarounds for invalidation of tanstack-query key over RTK Query endpoints.
// TODO: They must be removed as soon as mutations is migrated to tanstack-query
export const invalidateBuyingPlanInfo = () => {
  queryClient.invalidateQueries({
    queryKey: [QueryKey.BUYING_PLAN_INFO],
    type: 'active',
  });
};
const invalidateBuilderTableView = () => {
  queryClient.invalidateQueries({
    queryKey: [QueryKey.BUILDER_TABLE_VIEW],
    type: 'active',
  });
};

export const mediaPlansApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    updateMediaPlanRowOld: builder.mutation<unknown, UpdateRowPayload>({
      query: ({ rowId, ...data }) => ({
        url: `builder/media_plan_items/${rowId}/`,
        method: 'PUT',
        data,
      }),
      transformErrorResponse: (error: BaseQueryError) => {
        return transformValidationErrorResponse({
          error,
        });
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        await queryFulfilled;
        invalidateBuyingPlanInfo();
      },
    }),
    updateMediaPlanRow: builder.mutation<unknown, UpdateRowPayload>({
      query: ({ rowId, ...data }) => ({
        url: `builder/media_plan_items/${rowId}/`,
        method: 'PUT',
        data,
      }),
      transformErrorResponse: (error: BaseQueryError) =>
        formCustomRTKQueryError({
          snackbarOptions: { skipAppearance: true },
          data: error.data,
          status: error.status,
        }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        await queryFulfilled;
        invalidateBuyingPlanInfo();
      },
    }),
    updateMediaPlanRowLocked: builder.mutation<unknown, UpdateRowLockedPayload>(
      {
        query: ({ rowId, ...data }) => ({
          url: `builder/media_plan_items/${rowId}/`,
          method: 'PUT',
          data,
        }),
      },
    ),
    toggleCarbonNeutral: builder.mutation<
      // TODO:migration: fix any
      any,
      { id: string | number; isCarbonNeutralAllowed: boolean }
    >({
      query: ({ id, ...data }) => ({
        url: `campaign_collections/${id}/carbon_neutral/`,
        method: 'PATCH',
        data,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        await queryFulfilled;
        invalidateBuilderTableView();
      },
    }),
    addMediaPlanRow: builder.mutation<UpdateRowResponse, UpdateRowRequest>({
      query: (data) => ({
        url: 'builder/media_plan_items/',
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        await queryFulfilled;
        invalidateBuyingPlanInfo();
      },
    }),

    deleteMediaPlanRow: builder.mutation<void, { rowId: number }>({
      query: ({ rowId }) => ({
        url: `builder/media_plan_items/${rowId}/`,
        method: 'DELETE',
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        await queryFulfilled;
        invalidateBuyingPlanInfo();
      },
    }),
    updateMediaPlanRowsBudget: builder.mutation<
      void,
      { optimalBudgets: MediaPlanRowBudget[]; id: number }
    >({
      query: ({ id, optimalBudgets }) => ({
        url: `builder/media_plans/${id}/predictive_planning_update/`,
        method: 'PUT',
        data: optimalBudgets,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        await queryFulfilled;
        invalidateBuyingPlanInfo();
      },
    }),
    createPredictiveMediaPlanVersion: builder.mutation<
      void,
      { optimalBudgets: MediaPlanRowBudget[]; id: number }
    >({
      query: ({ id, optimalBudgets }) => ({
        url: `builder/media_plans/${id}/${PREDICTIVE_PLANNING_CREATE_AND_UPDATE}/`,
        method: 'POST',
        data: optimalBudgets,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        await queryFulfilled;
        invalidateBuyingPlanInfo();
        invalidateBuilderTableView();
      },
    }),
    createMediaPlanExelReport: builder.mutation<
      BlobResponse,
      { id: number; versionId: number }
    >({
      queryFn: async ({ id, versionId }) => {
        const res = await axiosApi.request({
          url: `builder/media_plans/${id}/export_as_excel/`,
          method: 'POST',
          data: {
            mediaPlanVersionId: versionId,
          },
          responseType: 'blob',
          baseURL: BASE_URL,
        });

        return {
          data: {
            blob: res.request.response as Blob,
            fileName:
              res.headers['content-disposition']?.split('filename=')?.[1] || id,
          },
        };
      },
    }),
  }),
});

export const {
  useToggleCarbonNeutralMutation,
  useUpdateMediaPlanRowOldMutation,
  useAddMediaPlanRowMutation,
  useDeleteMediaPlanRowMutation,
  useUpdateMediaPlanRowsBudgetMutation,
  useCreatePredictiveMediaPlanVersionMutation,
  useCreateMediaPlanExelReportMutation,
  useUpdateMediaPlanRowMutation,
  useUpdateMediaPlanRowLockedMutation,
  endpoints: mediaPlansApiEndpoints,
} = mediaPlansApi;
